// src/pages/AuditLogs.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText,
  Search,
  Filter,
  Eye,
  Shield,
  Clock,
  User,
  Building,
  Terminal,
  MessageSquare,
  Send,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Tag,
  Radio,
} from 'lucide-react';
import { AuditLog, PaginatedResult, TelegramLog, TelegramLogStats } from '../types';
import { useSettings } from '../contexts/SettingsContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { formatDateTime } from '../lib/date';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { EmptyState } from '../components/ui/EmptyState';

export const AuditLogs: React.FC = () => {
  const { settings } = useSettings();
  const { error: toastError } = useToast();

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });

  // Inspection Modal
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Tab State
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'telegram' ? 'telegram' : 'system';

  const setActiveTab = (tab: 'system' | 'telegram') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === 'telegram') next.set('tab', 'telegram');
      else next.delete('tab');
      return next;
    });
  };

  // Telegram Logs State
  const [telegramLogs, setTelegramLogs] = useState<TelegramLog[]>([]);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramSearch, setTelegramSearch] = useState('');
  const [telegramDirection, setTelegramDirection] = useState('');
  const [telegramStatusFilter, setTelegramStatusFilter] = useState('');
  const [telegramPage, setTelegramPage] = useState(1);
  const [telegramPagination, setTelegramPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [telegramStats, setTelegramStats] = useState<TelegramLogStats>({
    total_interactions: 0,
    total_inquiries: 0,
    total_responses: 0,
    unique_users: 0,
    unique_chats: 0,
    no_match_count: 0,
  });

  // Thread Inspection Modal
  const [selectedThreadChatId, setSelectedThreadChatId] = useState<string | null>(null);
  const [selectedThreadChatTitle, setSelectedThreadChatTitle] = useState<string>('');
  const [threadMessages, setThreadMessages] = useState<TelegramLog[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);

  const loadTelegramLogs = useCallback(async () => {
    try {
      setTelegramLoading(true);
      const params = new URLSearchParams({
        page: String(telegramPage),
        pageSize: settings.default_page_size || '25',
      });
      if (telegramSearch) params.append('search', telegramSearch);
      if (telegramDirection) params.append('direction', telegramDirection);
      if (telegramStatusFilter) params.append('status', telegramStatusFilter);

      const res = await apiRequest<any>(`/api/telegram/logs?${params.toString()}`);
      setTelegramLogs(res.items || []);
      setTelegramPagination(res.pagination);
      if (res.stats) setTelegramStats(res.stats);
    } catch (err: any) {
      toastError(err.message || 'Failed to load Telegram logs.');
    } finally {
      setTelegramLoading(false);
    }
  }, [telegramPage, telegramSearch, telegramDirection, telegramStatusFilter, settings.default_page_size, toastError]);

  const loadThread = async (chatId: string, chatTitle: string) => {
    setSelectedThreadChatId(chatId);
    setSelectedThreadChatTitle(chatTitle);
    setLoadingThread(true);
    try {
      const res = await apiRequest<any>(`/api/telegram/logs/threads/${encodeURIComponent(chatId)}`);
      setThreadMessages(res.messages || []);
    } catch (err: any) {
      toastError(err.message || 'Failed to load thread.');
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'telegram') {
      const timer = setTimeout(() => {
        loadTelegramLogs();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [activeTab, loadTelegramLogs]);

  const loadAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        pageSize: settings.default_page_size || '25',
      });
      if (search) params.append('search', search);
      if (moduleFilter) params.append('module', moduleFilter);
      if (actionFilter) params.append('action', actionFilter);

      const res = await apiRequest<PaginatedResult<AuditLog>>(`/api/audit-logs?${params.toString()}`);
      setLogs(res.items || []);
      setPagination(res.pagination);
    } catch (err: any) {
      toastError(err.message || 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [page, search, moduleFilter, actionFilter, settings.default_page_size, toastError]);

  useEffect(() => {
    if (activeTab === 'system') {
      const timer = setTimeout(() => {
        loadAuditLogs();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [activeTab, loadAuditLogs]);

  const getActionBadgeColor = (action: string) => {
    if (action.includes('CREATED') || action.includes('SUCCESS')) {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
    if (action.includes('DELETED') || action.includes('FAILED')) {
      return 'bg-rose-50 text-rose-700 border-rose-200';
    }
    if (action.includes('UPDATED') || action.includes('CHANGED') || action.includes('RESET')) {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  let formattedMetadata = '';
  if (selectedLog?.metadata_json) {
    try {
      formattedMetadata = JSON.stringify(JSON.parse(selectedLog.metadata_json), null, 2);
    } catch {
      formattedMetadata = selectedLog.metadata_json;
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with Navigation Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">System &amp; Interaction Audit</h2>
          <p className="text-xs text-slate-500">
            Immutable security trail recording administrative actions and real-time Telegram customer inquiries
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setActiveTab('system')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
              activeTab === 'system'
                ? 'bg-white text-slate-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Shield className="w-3.5 h-3.5 text-emerald-600" />
            <span>Platform Audits</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('telegram')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
              activeTab === 'telegram'
                ? 'bg-white text-sky-900 shadow-xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5 text-sky-600" />
            <span>Telegram Inquiries</span>
            {telegramStats.total_inquiries > 0 && (
              <span className="ml-0.5 px-1.5 py-0.2 rounded-full text-[10px] bg-sky-100 text-sky-700 font-bold">
                {telegramStats.total_inquiries}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'system' ? (
        <>
          {/* Filter Bar */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Input
                placeholder="Search action, actor, or payload..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                icon={<Search className="w-4 h-4" />}
              />
            </div>
            <div>
              <Select
                value={moduleFilter}
                onChange={e => {
                  setModuleFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { value: '', label: 'All Modules' },
                  { value: 'Authentication', label: 'Authentication' },
                  { value: 'Users', label: 'Users' },
                  { value: 'Departments', label: 'Departments' },
                  { value: 'Roles', label: 'Roles' },
                  { value: 'Data Records', label: 'Data Records' },
                  { value: 'Settings', label: 'Settings' },
                ]}
              />
            </div>
            <div>
              <Select
                value={actionFilter}
                onChange={e => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { value: '', label: 'All Actions' },
                  { value: 'LOGIN_SUCCESS', label: 'LOGIN_SUCCESS' },
                  { value: 'LOGIN_FAILED', label: 'LOGIN_FAILED' },
                  { value: 'LOGOUT', label: 'LOGOUT' },
                  { value: 'USER_CREATED', label: 'USER_CREATED' },
                  { value: 'USER_UPDATED', label: 'USER_UPDATED' },
                  { value: 'USER_DELETED', label: 'USER_DELETED' },
                  { value: 'PASSWORD_RESET', label: 'PASSWORD_RESET' },
                  { value: 'RECORD_CREATED', label: 'RECORD_CREATED' },
                  { value: 'RECORD_UPDATED', label: 'RECORD_UPDATED' },
                  { value: 'RECORD_DELETED', label: 'RECORD_DELETED' },
                  { value: 'PERMISSIONS_CHANGED', label: 'PERMISSIONS_CHANGED' },
                  { value: 'SETTINGS_UPDATED', label: 'SETTINGS_UPDATED' },
                ]}
              />
            </div>
          </div>

          {/* Audit Log Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <TableSkeleton rows={10} columns={6} />
            ) : logs.length === 0 ? (
              <EmptyState
                icon={<FileText className="w-10 h-10 text-slate-400" />}
                title="No audit events found"
                description="No system activity matches your filtering criteria."
              />
            ) : (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3.5">Timestamp</th>
                        <th className="px-6 py-3.5">Action</th>
                        <th className="px-6 py-3.5">Module</th>
                        <th className="px-6 py-3.5">Actor</th>
                        <th className="px-6 py-3.5">Department</th>
                        <th className="px-6 py-3.5">IP Address</th>
                        <th className="px-6 py-3.5 text-right">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {logs.map(log => (
                        <tr key={log.id} className="hover:bg-slate-50/70 transition">
                          <td className="px-6 py-4 text-xs font-mono text-slate-500 whitespace-nowrap">
                            {formatDateTime(log.created_at, settings.timezone)}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold font-mono border ${getActionBadgeColor(
                                log.action
                              )}`}
                            >
                              {log.action}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs font-medium text-slate-700">
                            {log.module}
                          </td>
                          <td className="px-6 py-4 text-xs text-slate-800">
                            <span className="font-semibold">{log.user_name || 'System / Unauth'}</span>
                            {log.user_id && (
                              <span className="text-[11px] text-slate-400 font-mono ml-1">
                                (UID:{log.user_id})
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-xs text-slate-500">
                            {log.department_name || (log.department_id ? `Dept #${log.department_id}` : '—')}
                          </td>
                          <td className="px-6 py-4 text-xs font-mono text-slate-500">
                            {log.ip_address || '—'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => setSelectedLog(log)}
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  totalPages={pagination.totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        /* Telegram Inquiries & Requests Tab */
        <div className="space-y-6">
          {/* Telegram KPI Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">Customer Inquiries</span>
              <span className="text-xl font-bold text-sky-700 mt-1 block">{telegramStats.total_inquiries}</span>
              <span className="text-[10px] text-slate-400 mt-0.5 block">Incoming user requests</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">Bot Responses</span>
              <span className="text-xl font-bold text-emerald-700 mt-1 block">{telegramStats.total_responses}</span>
              <span className="text-[10px] text-slate-400 mt-0.5 block">Automated price cards</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">Unique Users</span>
              <span className="text-xl font-bold text-slate-800 mt-1 block">{telegramStats.unique_users}</span>
              <span className="text-[10px] text-slate-400 mt-0.5 block">Telegram accounts</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">Active Chats</span>
              <span className="text-xl font-bold text-slate-800 mt-1 block">{telegramStats.unique_chats}</span>
              <span className="text-[10px] text-slate-400 mt-0.5 block">Groups &amp; private DMs</span>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs col-span-2 sm:col-span-1">
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">Suggestions Sent</span>
              <span className="text-xl font-bold text-amber-700 mt-1 block">{telegramStats.no_match_count}</span>
              <span className="text-[10px] text-slate-400 mt-0.5 block">Smart token match</span>
            </div>
          </div>

          {/* Telegram Filter Bar */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="sm:col-span-2">
              <Input
                placeholder="Search username, query, chat name, or ID..."
                value={telegramSearch}
                onChange={e => {
                  setTelegramSearch(e.target.value);
                  setTelegramPage(1);
                }}
                icon={<Search className="w-4 h-4" />}
              />
            </div>
            <div>
              <Select
                value={telegramDirection}
                onChange={e => {
                  setTelegramDirection(e.target.value);
                  setTelegramPage(1);
                }}
                options={[
                  { value: '', label: 'All Directions' },
                  { value: 'incoming', label: 'Incoming (User Queries)' },
                  { value: 'outgoing', label: 'Outgoing (Bot Replies)' },
                ]}
              />
            </div>
            <div>
              <Select
                value={telegramStatusFilter}
                onChange={e => {
                  setTelegramStatusFilter(e.target.value);
                  setTelegramPage(1);
                }}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'success', label: 'Success / Matched' },
                  { value: 'no_match', label: 'No Direct Match (Suggestions)' },
                  { value: 'error', label: 'Errors' },
                ]}
              />
            </div>
          </div>

          {/* Telegram Logs Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {telegramLoading ? (
              <TableSkeleton rows={10} columns={6} />
            ) : telegramLogs.length === 0 ? (
              <EmptyState
                icon={<MessageSquare className="w-10 h-10 text-slate-400" />}
                title="No Telegram interactions recorded"
                description="When sales reps or customers message @geobit_price_update_bot, their inquiries and conversation threads will be logged here."
              />
            ) : (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="px-5 py-3.5">Timestamp</th>
                        <th className="px-5 py-3.5">User / Sender</th>
                        <th className="px-5 py-3.5">Chat / Target</th>
                        <th className="px-5 py-3.5">Flow</th>
                        <th className="px-5 py-3.5">Command / Type</th>
                        <th className="px-5 py-3.5">Message / Inquiry</th>
                        <th className="px-5 py-3.5">Product Match</th>
                        <th className="px-5 py-3.5 text-right">Thread</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {telegramLogs.map(log => {
                        const isIncoming = log.direction === 'incoming';
                        return (
                          <tr key={log.id} className="hover:bg-slate-50/70 transition">
                            <td className="px-5 py-3.5 text-xs font-mono text-slate-500 whitespace-nowrap">
                              {formatDateTime(log.created_at, settings.timezone)}
                            </td>
                            <td className="px-5 py-3.5 text-xs text-slate-800">
                              <div className="font-semibold">{log.full_name || 'Anonymous'}</div>
                              {log.username && (
                                <div className="text-[11px] text-sky-600 font-mono">@{log.username}</div>
                              )}
                              {!log.username && log.user_id && (
                                <div className="text-[10px] text-slate-400 font-mono">ID: {log.user_id}</div>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-xs text-slate-700">
                              <div className="font-medium truncate max-w-[140px]">{log.chat_title || log.chat_id}</div>
                              <span className="inline-block text-[10px] uppercase tracking-wider text-slate-400 font-mono">
                                {log.chat_type || 'chat'} • {log.chat_id}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              {isIncoming ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                                  <ArrowDownLeft className="w-3 h-3 text-sky-600" />
                                  <span>User Query</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <ArrowUpRight className="w-3 h-3 text-emerald-600" />
                                  <span>Bot Reply</span>
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-xs font-mono text-slate-700">
                              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">
                                {log.command || (isIncoming ? 'query' : 'reply')}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-xs text-slate-800 max-w-xs">
                              <p className="line-clamp-2 font-mono text-[11px] text-slate-700 whitespace-pre-wrap">
                                {log.message_text}
                              </p>
                            </td>
                            <td className="px-5 py-3.5 text-xs">
                              {log.matched_product_name ? (
                                <span className="inline-flex items-center gap-1 font-medium text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 text-[11px]">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                  <span>#{log.matched_item_no}: {log.matched_product_name}</span>
                                </span>
                              ) : log.status === 'no_match' ? (
                                <span className="inline-flex items-center gap-1 font-medium text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-[11px]">
                                  <AlertCircle className="w-3 h-3 text-amber-600" />
                                  <span>Suggestions Provided</span>
                                </span>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-right">
                              <button
                                type="button"
                                onClick={() => loadThread(log.chat_id, log.chat_title || log.full_name || log.chat_id)}
                                className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 font-medium px-2 py-1 rounded hover:bg-sky-50 transition"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                Thread
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={telegramPagination.page}
                  pageSize={telegramPagination.pageSize}
                  total={telegramPagination.total}
                  totalPages={telegramPagination.totalPages}
                  onPageChange={setTelegramPage}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Detail Modal (System Logs) */}
      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="Audit Event Specification"
        description={`Event ID: #${selectedLog?.id} • Action: ${selectedLog?.action}`}
        maxWidth="lg"
      >
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs">
              <div>
                <p className="text-slate-400 font-medium">Actor</p>
                <p className="font-semibold text-slate-800">{selectedLog.user_name || 'System'}</p>
              </div>
              <div>
                <p className="text-slate-400 font-medium">Timestamp</p>
                <p className="font-semibold text-slate-800">
                  {formatDateTime(selectedLog.created_at, settings.timezone)}
                </p>
              </div>
              <div>
                <p className="text-slate-400 font-medium">Target Module</p>
                <p className="font-semibold text-slate-800">{selectedLog.module}</p>
              </div>
              <div>
                <p className="text-slate-400 font-medium">Record Reference ID</p>
                <p className="font-semibold text-slate-800">{selectedLog.record_id || 'N/A'}</p>
              </div>
              <div>
                <p className="text-slate-400 font-medium">IP Address</p>
                <p className="font-mono text-slate-800">{selectedLog.ip_address || 'N/A'}</p>
              </div>
              <div>
                <p className="text-slate-400 font-medium">Client User-Agent</p>
                <p className="text-[11px] text-slate-700 truncate" title={selectedLog.user_agent || ''}>
                  {selectedLog.user_agent || 'N/A'}
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-1.5 mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
                <Terminal className="w-3.5 h-3.5" />
                <span>Captured Snapshot Payload</span>
              </div>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs font-mono overflow-x-auto max-h-64 leading-relaxed">
                {formattedMetadata || 'No metadata parameters attached.'}
              </pre>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="secondary" onClick={() => setSelectedLog(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Telegram Threaded Conversation Modal */}
      <Modal
        isOpen={!!selectedThreadChatId}
        onClose={() => {
          setSelectedThreadChatId(null);
          setThreadMessages([]);
        }}
        title="Telegram Conversation Thread"
        description={`Threaded chat history for: ${selectedThreadChatTitle} (Chat ID: ${selectedThreadChatId})`}
        maxWidth="lg"
      >
        <div className="space-y-4">
          {loadingThread ? (
            <div className="py-8 text-center text-xs text-slate-500">Loading conversation thread...</div>
          ) : threadMessages.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-500">No messages found in this chat thread.</div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 max-h-[460px] overflow-y-auto space-y-3">
              {threadMessages.map(msg => {
                const isIncoming = msg.direction === 'incoming';
                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isIncoming ? 'items-start' : 'items-end'}`}
                  >
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1 px-1">
                      {isIncoming ? (
                        <>
                          <span className="font-semibold text-slate-800">{msg.full_name || 'Customer'}</span>
                          {msg.username && <span className="text-sky-600">(@{msg.username})</span>}
                          <span>•</span>
                          <span>{formatDateTime(msg.created_at, settings.timezone)}</span>
                        </>
                      ) : (
                        <>
                          <span>{formatDateTime(msg.created_at, settings.timezone)}</span>
                          <span>•</span>
                          <span className="font-semibold text-emerald-800">Geobit Bot</span>
                        </>
                      )}
                    </div>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-xs shadow-xs ${
                        isIncoming
                          ? 'bg-white border border-slate-200 text-slate-800 rounded-tl-xs'
                          : 'bg-emerald-900 text-emerald-50 rounded-tr-xs'
                      }`}
                    >
                      <div
                        className="whitespace-pre-wrap leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: msg.message_text.replace(/\n/g, '<br/>'),
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedThreadChatId(null);
                setThreadMessages([]);
              }}
            >
              Close Thread
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
